/**
 * Investigation form component (Module 24) — CoordioCore form-page-pattern.
 *
 * Layout: PageFormLayout (ShellHeader + SectionSideNav grid + panel sections)
 * Fields: @coordio/ui FormField (label + ⓘ tooltip + error + required asterisk)
 * Cards:  @coordio/ui FormSection (title + description + bordered body)
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Search, Users, CheckCircle as CheckCircleIcon } from 'lucide-react';

import {
  Button,
  CoordioDropdown,
  FormField,
  Input,
  Textarea,
} from '@coordio/ui';
import { FormSection } from '@coordio/ui/layout';

import { PageFormLayout } from '../layouts/PageFormLayout';
import { investigationService } from '../../services/investigationService';
import { agentService } from '../../services/agentService';
import { useNotificationStore } from '../../store/notificationStore';
import { ROUTES } from '../../utils/routes';
import type { AgentDefinitionDto } from '../../types/agent';
import {
  IncidentSeverities,
  InvestigationStatuses,
  type IncidentSeverity,
  type InvestigationStatus,
  type CreateInvestigationRequest,
  type UpdateInvestigationRequest,
  type InvestigationTemplateDto,
} from '../../types/investigation';

export const InvestigationForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { showNotification } = useNotificationStore();

  const [agents, setAgents] = useState<AgentDefinitionDto[]>([]);
  const [templates, setTemplates] = useState<InvestigationTemplateDto[]>([]);

  const [loading, setLoading] = useState(isEdit);
  const [loadingLookups, setLoadingLookups] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [agentDefinitionId, setAgentDefinitionId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<IncidentSeverity>(IncidentSeverities.Medium);
  const [incidentNumber, setIncidentNumber] = useState('');
  const [incidentOccurredAt, setIncidentOccurredAt] = useState('');
  const [templateId, setTemplateId] = useState<string>('');

  const [investigationStatus, setInvestigationStatus] = useState<InvestigationStatus>(InvestigationStatuses.NotStarted);
  const [rootCauseSummary, setRootCauseSummary] = useState('');
  const [remediationSteps, setRemediationSteps] = useState('');
  const [preventionRecommendations, setPreventionRecommendations] = useState('');

  const severityOptions = useMemo(
    () => [
      { value: IncidentSeverities.Low, label: 'Low' },
      { value: IncidentSeverities.Medium, label: 'Medium' },
      { value: IncidentSeverities.High, label: 'High' },
      { value: IncidentSeverities.Critical, label: 'Critical' },
    ],
    []
  );

  const isCompleted = investigationStatus === InvestigationStatuses.Completed;

  const statusOptions = useMemo(() => {
    const options: { value: InvestigationStatus; label: string }[] = [
      { value: InvestigationStatuses.NotStarted, label: 'Not Started' },
      { value: InvestigationStatuses.InProgress, label: 'In Progress' },
      { value: InvestigationStatuses.Paused, label: 'Paused' },
      { value: InvestigationStatuses.Cancelled, label: 'Cancelled' },
    ];
    if (isCompleted) {
      options.push({ value: InvestigationStatuses.Completed, label: 'Completed' });
    }
    return options;
  }, [isCompleted]);

  useEffect(() => {
    loadLookups();
    if (isEdit && id) {
      loadInvestigation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadLookups = async () => {
    setLoadingLookups(true);
    try {
      const [agentsResult, templatesResult] = await Promise.all([
        agentService.getAgents({ pageSize: 1000, isActive: true }),
        investigationService.getInvestigationTemplates(true),
      ]);
      setAgents(agentsResult.items);
      setTemplates(templatesResult);
    } catch (err: any) {
      showNotification(err.message || 'Failed to load lookup data', 'error');
    } finally {
      setLoadingLookups(false);
    }
  };

  const loadInvestigation = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const inv = await investigationService.getInvestigation(id);
      setAgentDefinitionId(inv.agentDefinitionId);
      setTitle(inv.title);
      setDescription(inv.description || '');
      setSeverity(inv.severity);
      setIncidentNumber(inv.incidentNumber || '');
      setIncidentOccurredAt(inv.incidentOccurredAt ? inv.incidentOccurredAt.slice(0, 16) : '');
      setInvestigationStatus(inv.investigationStatus);
      setRootCauseSummary(inv.rootCauseSummary || '');
      setRemediationSteps(inv.remediationSteps || '');
      setPreventionRecommendations(inv.preventionRecommendations || '');
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to load investigation';
      setError(msg);
      showNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (!agentDefinitionId) {
      setError('Agent is required');
      return;
    }

    setSaving(true);
    try {
      if (isEdit && id) {
        const request: UpdateInvestigationRequest = {
          title: title.trim(),
          description: description.trim() || undefined,
          severity,
          investigationStatus: isCompleted ? undefined : investigationStatus,
          preventionRecommendations: preventionRecommendations.trim() || undefined,
        };
        await investigationService.updateInvestigation(id, request);
        showNotification('Investigation updated successfully', 'success');
      } else {
        const request: CreateInvestigationRequest = {
          agentDefinitionId,
          title: title.trim(),
          description: description.trim() || undefined,
          severity,
          incidentNumber: incidentNumber.trim() || undefined,
          incidentOccurredAt: incidentOccurredAt ? new Date(incidentOccurredAt).toISOString() : undefined,
          templateId: templateId || undefined,
        };
        await investigationService.createInvestigation(request);
        showNotification('Investigation created successfully', 'success');
      }

      navigate(ROUTES.INVESTIGATIONS);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to save investigation';
      setError(msg);
      showNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    if (!id || isCompleted) return;
    setError(null);
    setSaving(true);
    try {
      await investigationService.completeInvestigation(id, {
        rootCauseSummary: rootCauseSummary.trim() || undefined,
        remediationSteps: remediationSteps.trim() || undefined,
        preventionRecommendations: preventionRecommendations.trim() || undefined,
      });
      showNotification('Investigation completed successfully', 'success');
      navigate(ROUTES.INVESTIGATIONS);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to complete investigation';
      setError(msg);
      showNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageFormLayout
      title={isEdit ? 'Edit Investigation' : 'Create Investigation'}
      subtitle="Create or manage an incident investigation with agent-assisted root cause analysis."
      data-testid="investigation-form-page"
      loading={loading || loadingLookups}
      error={error ?? undefined}
      onErrorDismiss={() => setError(null)}
      backTo={{ label: 'Back to List', href: ROUTES.INVESTIGATIONS }}
      actions={{
        onSave: handleSubmit,
        onCancel: () => navigate(ROUTES.INVESTIGATIONS),
        saveLabel: isEdit ? 'Update' : 'Create',
        saving,
      }}
      headerActions={
        isEdit && !isCompleted ? (
          <Button
            type="button"
            variant="outline"
            className="border-green-500 text-green-600 hover:bg-green-50"
            disabled={saving}
            onClick={handleComplete}
          >
            <CheckCircleIcon className="mr-2 h-4 w-4" />
            Complete
          </Button>
        ) : undefined
      }
      navGroups={[
        {
          title: 'Profile',
          items: [
            { id: 'details', label: 'Details', icon: <Search size={14} /> },
          ],
        },
        {
          title: 'Configuration',
          items: [
            { id: 'assignment', label: 'Assignment', icon: <Users size={14} /> },
          ],
        },
      ]}
      sections={[
        {
          id: 'details',
          title: 'Details',
          description: 'Name, description, severity, and status of the investigation.',
          icon: <Search size={16} />,
          content: (
            <FormSection
              title="Core details"
              description="Identify the investigation and set its priority."
            >
              <FormField
                label="Title"
                required
                description="A concise name for this investigation."
                data-testid="investigation-title"
              >
                <Input
                  className="w-full"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  data-testid="investigation-title"
                />
              </FormField>

              <div className="mt-5">
                <FormField label="Description" description="Optional details about the incident being investigated.">
                  <Textarea
                    className="w-full"
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    data-testid="investigation-description"
                  />
                </FormField>
              </div>

              <div className="mt-5 grid gap-5 items-end sm:grid-cols-2">
                <FormField label="Severity" description="Priority level of the incident.">
                  <CoordioDropdown
                    options={severityOptions.map((opt) => ({ label: opt.label, value: String(opt.value) }))}
                    value={String(severity)}
                    onChange={(v) => setSeverity(Number(v) as IncidentSeverity)}
                    clearable={false}
                    data-testid="investigation-severity"
                  />
                </FormField>

                {isEdit && (
                  <FormField label="Status" description="Current status of the investigation.">
                    <CoordioDropdown
                      options={statusOptions.map((opt) => ({ label: opt.label, value: String(opt.value) }))}
                      value={String(investigationStatus)}
                      onChange={(v) => setInvestigationStatus(Number(v) as InvestigationStatus)}
                      clearable={false}
                      disabled={isCompleted}
                      data-testid="investigation-status"
                    />
                  </FormField>
                )}
              </div>
            </FormSection>
          ),
        },
        {
          id: 'assignment',
          title: 'Assignment',
          description: 'Assign the agent, template, and provide incident parameters.',
          icon: <Users size={16} />,
          content: (
            <FormSection
              title="Agent &amp; template"
              description="Select the investigating agent and optional template."
            >
              <div className="grid gap-5 items-end sm:grid-cols-2">
                <FormField label="Agent" required description="The agent that will perform the investigation.">
                  <CoordioDropdown
                    options={agents.map((a) => ({ label: a.name, value: a.id }))}
                    value={agentDefinitionId || null}
                    onChange={(v) => setAgentDefinitionId(v ?? '')}
                    clearable={false}
                    disabled={isEdit}
                    data-testid="investigation-agent"
                  />
                </FormField>

                {!isEdit && (
                  <FormField label="Template" description="Optional template to pre-fill investigation steps.">
                    <CoordioDropdown
                      options={[
                        { label: 'None', value: '' },
                        ...templates.map((t) => ({ label: t.name, value: t.id })),
                      ]}
                      value={templateId || null}
                      onChange={(v) => setTemplateId(v ?? '')}
                      clearable={false}
                      data-testid="investigation-template"
                    />
                  </FormField>
                )}
              </div>

              {!isEdit && (
                <div className="mt-5 grid gap-5 items-end sm:grid-cols-2">
                  <FormField label="Incident Number" description="External tracking reference (e.g., INC-12345).">
                    <Input
                      className="w-full"
                      value={incidentNumber}
                      onChange={(e) => setIncidentNumber(e.target.value)}
                      placeholder="INC-12345"
                      data-testid="investigation-incidentNumber"
                    />
                  </FormField>

                  <FormField label="Incident Occurred At" description="When the incident originally happened.">
                    <Input
                      className="w-full"
                      type="datetime-local"
                      value={incidentOccurredAt}
                      onChange={(e) => setIncidentOccurredAt(e.target.value)}
                      data-testid="investigation-incidentOccurredAt"
                    />
                  </FormField>
                </div>
              )}

              {isEdit && (
                <>
                  <div className="mt-5">
                    <FormField label="Root Cause Summary" description="Summary of the identified root cause. Saved when you complete the investigation.">
                      <Textarea
                        className="w-full"
                        rows={3}
                        value={rootCauseSummary}
                        onChange={(e) => setRootCauseSummary(e.target.value)}
                        disabled={isCompleted}
                        data-testid="investigation-rootCauseSummary"
                      />
                    </FormField>
                  </div>

                  <div className="mt-5">
                    <FormField label="Remediation Steps" description="Actions taken to address the incident. Saved when you complete the investigation.">
                      <Textarea
                        className="w-full"
                        rows={3}
                        value={remediationSteps}
                        onChange={(e) => setRemediationSteps(e.target.value)}
                        disabled={isCompleted}
                        data-testid="investigation-remediationSteps"
                      />
                    </FormField>
                  </div>

                  <div className="mt-5">
                    <FormField label="Prevention Recommendations" description="Suggestions to prevent future recurrence.">
                      <Textarea
                        className="w-full"
                        rows={3}
                        value={preventionRecommendations}
                        onChange={(e) => setPreventionRecommendations(e.target.value)}
                        data-testid="investigation-preventionRecommendations"
                      />
                    </FormField>
                  </div>
                </>
              )}
            </FormSection>
          ),
        },
      ]}
    />
  );
};
